import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Controls,
  ReactFlow,
  ReactFlowProvider,
  useNodesInitialized,
  useNodesState,
  useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import LogoLoader from "../../common/LogoLoader";
import { useEcosystem } from "../../../hooks/queries/useEcosystem";
import { useProductSearch } from "../../../hooks/queries/useProductSearch";
import {
  EcosystemParams,
  ProductSearchParams,
} from "../../../services/productPlaneApi";
import { ProductCard } from "../../../types/productPlane";
import {
  Ecosystem,
  EcosystemCentreType,
  EcosystemEdgeType,
  EcosystemNode,
  EcosystemNodeType,
} from "../../../types/analytics";
import { ExportFacts } from "../../../utils/export/ExportFrame";
import { useCapturing } from "../../../utils/export/ExportStage";
import { SEGMENT_SX, TOOL_BUTTON_SX } from "../../ValueChain/ValueChainControls";
import { AnalyticsPaneProps } from "../analyticsPanes";
import {
  ANALYTICS_VIEW_LABELS,
  ECOSYSTEM_CENTRE_TYPES,
  ECOSYSTEM_DEPTHS,
  EcosystemDepth,
  EcosystemSettings,
  selectionOf,
  toEcosystemParams,
} from "../analyticsState";
import {
  EcosystemLayout,
  focusRoutes,
  GLYPH_SIZES,
  LABEL_WIDTH,
  layoutEcosystem,
} from "./ecosystemLayout";
import EcosystemPanel from "./EcosystemPanel";
import {
  EcoEdgeLine,
  EcoFlowEdge,
  EcoFlowNode,
  EcoNode,
  Glyph,
  LineSample,
  NODE_TYPE_LABELS,
  RingFlowNode,
  RingNode,
  centreKindsText,
  counted,
  edgeReading,
  lineLabel,
  nodeName,
  strongestFirst,
  structureLine,
  structureLink,
} from "./EcosystemParts";

type EcoCanvasNode = RingFlowNode | EcoFlowNode;

const NODE_TYPES = { ring: RingNode, eco: EcoNode };
const EDGE_TYPES = { eco: EcoEdgeLine };
const FIT_VIEW_OPTIONS = { padding: 0.04, maxZoom: 1.15 };
// Products offered as a centre while none is chosen
const SUGGESTION_LIMIT = 20;
// The floor a node is measured against. Around a kind a node's count is a
// roll-up over the kind's products, which a floor can call thin. Around one
// product, organization or programme a neighbour counts what it shares with
// that one centre: one is then the whole of what there is, not a thin count.
export const supportFloor = (eco: Ecosystem): number =>
  eco.centre_type === "kind" ? eco.floors.min_support : 0;

// The floors in force, as a caption
export const floorsCaption = (eco: Ecosystem): string =>
  [
    ...(supportFloor(eco) > 0
      ? [`a node needs ${counted(supportFloor(eco), "product")} to count firmly`]
      : []),
    `at most ${eco.floors.top_n} nodes per ring`,
    ...(eco.thin_scope
      ? [`the scope holds under ${counted(eco.floors.min_scope, "product")}: every number is a hint`]
      : []),
  ].join(" · ");

interface CentreOption {
  id: string;
  type: EcosystemCentreType;
  name: string;
  // Found by the search for the typed text, which may match it by more
  // than its name: kept however its name reads
  found?: boolean;
}

const optionOf = (node: EcosystemNode): CentreOption => ({
  id: node.id,
  type: node.type,
  name: nodeName(node),
});

const cardOption = (card: ProductCard, found = false): CentreOption => ({
  id: card.id,
  type: "product",
  name: card.name ?? card.id,
  found,
});

// How long the typed text rests before the scope is searched for it
export const SEARCH_DELAY_MS = 250;

// Whether a node stands under the floor: it counts fewer products than a
// node needs to count firmly. A product is one product and the centre is
// the subject, so neither is measured against it.
export const underFloor = (node: EcosystemNode, minSupport: number): boolean =>
  node.ring > 0 && node.type !== "product" && node.product_count < minSupport;

// A kind is drawn only as the centre: the legend names it only where one
// stands around it
const LEGEND_TYPES: EcosystemNodeType[] = ["product", "organization", "project", "kind"];
// The darker links, which the legend names as one
const LINK_TYPES: Exclude<EcosystemEdgeType, "structure">[] = [
  "role",
  "programme",
  "participation",
  "kind",
];
// The darker links in words, as the legend names them
const LINK_WORDS: Record<Exclude<EcosystemEdgeType, "structure">, string> = {
  role: "role",
  programme: "programme",
  participation: "programme part",
  kind: "kind",
};

// Words in a list: "a, b or c"
const listed = (words: string[]): string =>
  words.length > 1
    ? `${words.slice(0, -1).join(", ")} or ${words[words.length - 1]}`
    : words.join("");

const LegendEntry: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box component="li" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
    {children}
  </Box>
);

// What the marks of the graph mean, under it, in plain words: only the
// marks drawn
const Legend: React.FC<{ eco: Ecosystem }> = ({ eco }) => {
  const around = eco.nodes.filter((node) => node.ring > 0);
  const thin = around.find((node) => underFloor(node, supportFloor(eco)));
  const outside = around.find((node) => node.outside);
  const drawn = new Set(eco.edges.map((edge) => edge.type));
  const links = LINK_TYPES.filter((type) => drawn.has(type));
  // How the links between products are drawn: a part or payload, a
  // variant, another stated link
  const lines = new Set(
    eco.edges
      .filter((edge) => edge.type === "structure")
      .map((edge) => structureLine(structureLink(edge))),
  );
  const counts = around.some((node) => node.type !== "product");
  return (
    <Box
      component="ul"
      aria-label="Legend"
      sx={{
        m: 0,
        p: 0,
        listStyle: "none",
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        columnGap: 2,
        rowGap: 1,
        fontSize: 12,
        color: "text.secondary",
      }}
    >
      <LegendEntry>
        <Glyph type={eco.centre_type} tone="centre" sample />
        centre
      </LegendEntry>
      {LEGEND_TYPES.filter((type) => around.some((node) => node.type === type)).map(
        (type) => (
          <LegendEntry key={type}>
            <Glyph type={type} tone="plain" sample />
            {NODE_TYPE_LABELS[type]}
          </LegendEntry>
        ),
      )}
      {outside && (
        <LegendEntry>
          <Glyph type={outside.type} tone="outside" sample />
          faint = outside the selection, reached through a stated link
        </LegendEntry>
      )}
      {thin && (
        <LegendEntry>
          <Glyph type={thin.type} tone="thin" sample />
          hatched = fewer than {counted(supportFloor(eco), "product")}
        </LegendEntry>
      )}
      {counts && (
        <LegendEntry>number at a mark = the products behind it</LegendEntry>
      )}
      {lines.has("composition") && (
        <LegendEntry>
          <LineSample type="structure" relation="has_component" />
          light line = a part or a payload and what holds it
        </LegendEntry>
      )}
      {lines.has("variant") && (
        <LegendEntry>
          <LineSample type="structure" relation="variant_of" />
          dashes = a variant and the product it varies
        </LegendEntry>
      )}
      {lines.has("other") && (
        <LegendEntry>
          <LineSample type="structure" relation="uses" />
          thin line = another stated link: a system used, an interface
        </LegendEntry>
      )}
      {links.length > 0 && (
        <LegendEntry>
          <LineSample type={links[0]} />
          darker line = a {listed(links.map((type) => LINK_WORDS[type]))} link
        </LegendEntry>
      )}
      <LegendEntry>
        <LineSample type="role" lit />
        accent line = the lines of the node in focus; fainter, its way to the centre
      </LegendEntry>
    </Box>
  );
};

interface CanvasProps {
  layout: EcosystemLayout;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  // Products a node needs to count firmly
  minSupport: number;
  // The kinds the centre falls under, beneath its name
  centreKinds: string | null;
  // Counts the presses of Fit: each one fits the view again
  fitTick: number;
}

// The ring guides, nodes and lines of a layout; the layout is never run
// again by a render, only by a new response
const EcosystemCanvas: React.FC<CanvasProps> = ({
  layout,
  selectedId,
  onSelect,
  minSupport,
  centreKinds,
  fitTick,
}) => {
  const theme = useTheme();
  // The zoom buttons operate the graph; an exported image has none
  const capturing = useCapturing();
  const [nodes, setNodes, onNodesChange] = useNodesState<EcoCanvasNode>([]);
  const { fitView } = useReactFlow<EcoCanvasNode>();
  const measured = useNodesInitialized();
  // The node the pointer or the keyboard is on
  const [pointedId, setPointedId] = useState<string | null>(null);

  const placedById = useMemo(
    () => new Map(layout.nodes.map((entry) => [entry.node.id, entry.node])),
    [layout],
  );
  // The node whose lines are lit: the one pointed at, else the selected one
  const onGraph = (id: string | null) =>
    id !== null && placedById.has(id) ? id : null;
  const focusId = onGraph(pointedId) ?? onGraph(selectedId);

  // Its own lines, and the routes by which it reaches the centre
  const lit = useMemo(() => focusRoutes(layout, focusId), [layout, focusId]);
  const partners = lit.nodes;

  const pick = useCallback((id: string) => onSelect(id), [onSelect]);

  useEffect(() => {
    const guides: RingFlowNode[] = layout.rings.map((ring) => ({
      id: `ring:${ring.ring}`,
      type: "ring",
      position: { x: -ring.rx, y: -ring.ry },
      data: { ...ring },
      style: { width: ring.rx * 2, height: ring.ry * 2 },
      zIndex: -1,
      draggable: false,
      selectable: false,
      focusable: false,
    }));
    // A node is placed by the centre of its glyph; its name hangs beneath
    const placed: EcoFlowNode[] = layout.nodes.map((entry) => ({
      id: entry.node.id,
      type: "eco",
      position: {
        x: entry.x - LABEL_WIDTH / 2,
        y: entry.y - GLYPH_SIZES[entry.node.type].height / 2,
      },
      data: {
        node: entry.node,
        selected: false,
        dimmed: false,
        thin: underFloor(entry.node, minSupport),
        centreKinds: entry.node.ring === 0 ? centreKinds : null,
        onSelect: pick,
        onPoint: setPointedId,
      },
      draggable: false,
      connectable: false,
    }));
    setNodes([...guides, ...placed]);
  }, [layout, pick, minSupport, centreKinds, setNodes]);

  // The view is fitted once the names are measured, on every new layout
  // and on every press of Fit
  useEffect(() => {
    if (measured) {
      void fitView(FIT_VIEW_OPTIONS);
    }
  }, [measured, layout, fitTick, fitView]);

  // A change of selection or of the node pointed at lights and dims in place
  useEffect(() => {
    setNodes((current) =>
      current.map((node) => {
        if (node.type !== "eco") {
          return node;
        }
        const isSelected = node.id === selectedId;
        const dimmed =
          focusId !== null &&
          node.id !== focusId &&
          !isSelected &&
          !partners.has(node.id);
        const data = node.data as EcoFlowNode["data"];
        return data.selected === isSelected && data.dimmed === dimmed
          ? node
          : { ...node, data: { ...data, selected: isSelected, dimmed } };
      }),
    );
  }, [layout, selectedId, focusId, partners, setNodes]);

  const edges = useMemo<EcoFlowEdge[]>(
    () =>
      layout.edges.map((placed) => {
        const active = lit.direct.has(placed.id);
        const route = lit.route.has(placed.id);
        const held = strongestFirst(placed.edges);
        return {
          id: placed.id,
          type: "eco",
          source: held[0].source,
          target: held[0].target,
          selectable: false,
          focusable: false,
          data: {
            x1: placed.x1,
            y1: placed.y1,
            x2: placed.x2,
            y2: placed.y2,
            edge: held[0],
            reading: held
              .map((edge) =>
                edgeReading(
                  edge,
                  placedById.get(edge.source),
                  placedById.get(edge.target),
                ),
              )
              .join("\n"),
            // The lines of the node in focus are labelled
            label: active ? lineLabel(held) : null,
            active,
            route,
            dimmed: focusId !== null && !active && !route,
          },
        };
      }),
    [layout, placedById, focusId, lit],
  );

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChange}
      nodeTypes={NODE_TYPES}
      edgeTypes={EDGE_TYPES}
      onPaneClick={() => onSelect(null)}
      nodesDraggable={false}
      nodesConnectable={false}
      fitView
      fitViewOptions={FIT_VIEW_OPTIONS}
      minZoom={0.2}
      maxZoom={1.5}
      colorMode={theme.palette.mode}
      proOptions={{ hideAttribution: true }}
    >
      {!capturing && <Controls showInteractive={false} showFitView={false} />}
    </ReactFlow>
  );
};

const isNotFound = (error: unknown): boolean =>
  (error as { response?: { status?: number } } | null)?.response?.status === 404;

// The ecosystem: the graph of the products, organizations, programmes and
// kinds around a centre, in rings by distance from it. While no centre is
// chosen the view asks for one and offers the products of the scope.
const EcosystemView: React.FC<AnalyticsPaneProps> = ({
  state,
  onChange,
  onOpenEntity,
}) => {
  const settings = state.settings.ecosystem;
  const hasCentre = settings.centreId !== "";
  const params = useMemo(() => toEcosystemParams(state), [state]);
  const read = useEcosystem(params, hasCentre);
  const eco: Ecosystem | null = hasCentre ? (read.data ?? null) : null;
  const layout = useMemo(() => (eco ? layoutEcosystem(eco) : null), [eco]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [fitTick, setFitTick] = useState(0);

  // The scope's own products, to pick a first centre from
  const searchParams = useMemo<ProductSearchParams>(() => {
    const scope: ProductSearchParams = { ...params, limit: SUGGESTION_LIMIT };
    delete (scope as EcosystemParams).depth;
    return scope;
  }, [params]);
  const suggestions = useProductSearch(searchParams, !hasCentre);
  const suggested = useMemo<CentreOption[]>(
    () => (suggestions.data?.results.items ?? []).map((card: ProductCard) => cardOption(card)),
    [suggestions.data],
  );

  // Any product of the scope can be the centre: the typed text, once it
  // rests, is searched for among them, beside the scope's own keyword
  const [typed, setTyped] = useState("");
  const [query, setQuery] = useState("");
  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(typed.trim()), SEARCH_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [typed]);
  const matchParams = useMemo<ProductSearchParams>(() => {
    const scope = { ...params, limit: SUGGESTION_LIMIT } as EcosystemParams &
      ProductSearchParams;
    delete scope.centre_type;
    delete scope.centre_id;
    delete scope.depth;
    scope.keyword = [params.keyword, query].filter(Boolean).join(" ");
    return scope;
  }, [params, query]);
  const matches = useProductSearch(matchParams, query !== "");
  const matched = useMemo<CentreOption[]>(
    () =>
      query === ""
        ? []
        : (matches.data?.results.items ?? []).map((card: ProductCard) => cardOption(card, true)),
    [matches.data, query],
  );

  const node = eco?.nodes.find((candidate) => candidate.id === selectedId) ?? null;
  useEffect(() => {
    if (selectedId !== null && eco && !node) {
      setSelectedId(null);
    }
  }, [selectedId, eco, node]);

  const setSettings = useCallback(
    (next: EcosystemSettings) => {
      onChange({ settings: { ...state.settings, ecosystem: next } });
    },
    [onChange, state.settings],
  );

  const centreOn = useCallback(
    (type: EcosystemCentreType, id: string) => {
      setSelectedId(null);
      setSettings({ ...settings, centreType: type, centreId: id });
    },
    [setSettings, settings],
  );

  // Grouped by type, so that each group's header appears once; the products
  // the search found first, then those listed already, each once
  const options = useMemo<CentreOption[]>(() => {
    const listed = eco
      ? eco.nodes.filter((entry) => entry.ring > 0).map(optionOf)
      : suggested;
    const seen = new Set<string>();
    const unique = [...matched, ...listed].filter((option) => {
      const key = `${option.type}:${option.id}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
    return ECOSYSTEM_CENTRE_TYPES.flatMap((type) =>
      unique.filter((option) => option.type === type),
    );
  }, [eco, suggested, matched]);

  const centreName = eco ? (eco.centre.name ?? eco.centre.id) : settings.centreId;
  const drawn = eco !== null && layout !== null && eco.nodes.length > 1;
  // In an exported image: the centre and the depth said in words, in place
  // of the controls that set them, and no panel
  const capturing = useCapturing();

  return (
    <Box
      component="section"
      aria-label={ANALYTICS_VIEW_LABELS.ecosystem}
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        gap: 1.75,
      }}
    >
      {capturing ? (
        hasCentre && (
          <ExportFacts
            aria-label="Centre"
            facts={[
              ["Centre", `${NODE_TYPE_LABELS[settings.centreType]}: ${centreName}`],
              ["Depth", settings.depth === 1 ? "1 ring" : "2 rings"],
            ]}
          />
        )
      ) : (
        <Box sx={{ display: "flex", gap: 1.25, alignItems: "center", flexWrap: "wrap" }}>
          <Autocomplete<CentreOption>
            size="small"
            sx={{
              flex: "0 1 400px",
              minWidth: 260,
              "& .MuiOutlinedInput-root": {
                minHeight: 36,
                py: "0 !important",
                flexWrap: "nowrap",
                gap: 1,
                fontSize: 13,
                backgroundColor: "background.paper",
              },
              "& .MuiOutlinedInput-root .MuiAutocomplete-input": {
                py: 0,
                minWidth: 60,
              },
            }}
            options={options}
            value={null}
            inputValue={typed}
            onInputChange={(_event, next, reason) => {
              // A pick fills the field with its name: the field empties instead
              setTyped(reason === "reset" ? "" : next);
            }}
            // The listed options by their name; the found ones as found
            filterOptions={(candidates, { inputValue }) => {
              const text = inputValue.trim().toLocaleLowerCase();
              return candidates.filter(
                (option) => option.found || option.name.toLocaleLowerCase().includes(text),
              );
            }}
            loading={query !== "" && matches.isFetching}
            groupBy={(option) => NODE_TYPE_LABELS[option.type]}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(a, b) => a.type === b.type && a.id === b.id}
            noOptionsText={
              typed.trim() !== ""
                ? "No product of this scope by that name"
                : hasCentre
                  ? "Nothing around the centre"
                  : "No product in this scope"
            }
            onChange={(_event, picked) => {
              if (picked) {
                setTyped("");
                centreOn(picked.type, picked.id);
              }
            }}
            renderInput={(inputParams) => (
              <TextField
                {...inputParams}
                placeholder={hasCentre ? "Re-centre on…" : "Type a name"}
                inputProps={{
                  ...inputParams.inputProps,
                  "aria-label": hasCentre ? "Re-centre on" : "Centre",
                }}
                InputProps={{
                  ...inputParams.InputProps,
                  // The field names itself and holds the centre in force
                  startAdornment: (
                    <>
                      <Box
                        component="span"
                        aria-hidden
                        sx={{
                          flexShrink: 0,
                          fontSize: 11,
                          fontWeight: 500,
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                          color: "text.secondary",
                        }}
                      >
                        Centre
                      </Box>
                      {hasCentre && (
                        <Chip
                          label={`${NODE_TYPE_LABELS[settings.centreType]}: ${centreName}`}
                          onDelete={() => centreOn(settings.centreType, "")}
                          size="small"
                          aria-label="Current centre"
                          sx={{ maxWidth: 220 }}
                        />
                      )}
                    </>
                  ),
                }}
              />
            )}
          />
          <ToggleButtonGroup
            exclusive
            size="small"
            value={settings.depth}
            aria-label="Depth"
            onChange={(_event, next: EcosystemDepth | null) => {
              if (next) {
                setSettings({ ...settings, depth: next });
              }
            }}
          >
            {ECOSYSTEM_DEPTHS.map((depth) => (
              <ToggleButton
                key={depth}
                value={depth}
                aria-label={`Depth ${depth}`}
                sx={{ ...SEGMENT_SX, height: 36 }}
              >
                {depth === 1 ? "1 ring" : "2 rings"}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Button
            variant="outlined"
            size="small"
            disabled={!drawn}
            onClick={() => setFitTick((tick) => tick + 1)}
            sx={{ ...TOOL_BUTTON_SX, ml: "auto" }}
          >
            Fit
          </Button>
        </Box>
      )}

      <Box
        sx={{
          position: "relative",
          flex: 1,
          display: "flex",
          minHeight: 560,
          border: 1,
          borderColor: "divider",
          borderRadius: (theme) => `${theme.radius.surface}px`,
          backgroundColor: "background.paper",
          overflow: "hidden",
        }}
      >
        {!hasCentre ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
              p: 3,
            }}
          >
            <Typography color="text.secondary" align="center">
              Pick a centre for the ecosystem: a product of this scope below,
              or any product of this scope by name in the field above.
            </Typography>
            {suggestions.isLoading ? (
              <CircularProgress size={24} aria-label="Loading suggestions" />
            ) : (
              <Box
                role="list"
                aria-label="Suggested centres"
                sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "center" }}
              >
                {suggested.map((option) => (
                  <Box component="span" role="listitem" key={option.id}>
                    <Chip
                      label={option.name}
                      size="small"
                      variant="outlined"
                      onClick={() => centreOn(option.type, option.id)}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        ) : !eco || !layout ? (
          <Box sx={{ flex: 1, display: "grid", placeItems: "center", p: 3 }}>
            {read.isLoading ? (
              <LogoLoader size="view" surface="paper" label="Loading the ecosystem" />
            ) : isNotFound(read.error) ? (
              <Box sx={{ textAlign: "center" }}>
                <Typography color="text.secondary" gutterBottom>
                  Centre not found: {settings.centreId}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => centreOn(settings.centreType, "")}
                  sx={{ textTransform: "none" }}
                >
                  Clear centre
                </Button>
              </Box>
            ) : (
              <Typography color="text.secondary">
                The ecosystem could not be loaded.
              </Typography>
            )}
          </Box>
        ) : eco.nodes.length <= 1 ? (
          <Box sx={{ flex: 1, display: "grid", placeItems: "center", p: 3 }}>
            <Typography color="text.secondary" align="center">
              Nothing around {centreName} in this scope: the{" "}
              {counted(eco.evidence.n, "product")} in it link it to no other
              product, organization, programme or kind.
            </Typography>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                // The surface shows through: no second ground under the graph
                "& .react-flow": { backgroundColor: "transparent" },
              }}
            >
              <ReactFlowProvider>
                <EcosystemCanvas
                  layout={layout}
                  selectedId={selectedId}
                  onSelect={setSelectedId}
                  minSupport={supportFloor(eco)}
                  centreKinds={centreKindsText(eco)}
                  fitTick={fitTick}
                />
              </ReactFlowProvider>
            </Box>
            {node && !capturing && (
              <EcosystemPanel
                eco={eco}
                node={node}
                selection={selectionOf(state)}
                onSelect={setSelectedId}
                onRecentre={(next) => centreOn(next.type, next.id)}
                onOpenEntity={onOpenEntity}
              />
            )}
          </>
        )}
      </Box>

      {eco && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          {drawn && <Legend eco={eco} />}
          <Typography
            variant="caption"
            component="div"
            color="text.disabled"
            aria-label="Floors"
          >
            {floorsCaption(eco)}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default EcosystemView;
